'use client';

import { useState, useMemo, useRef, useCallback } from 'react';
import { Calendar, X } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverAnchor,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';

export interface CalendarSession {
  id: string;
  date?: string;
  startTime: string;
  endTime: string;
  sessionType?: string | null;
  courtNumber?: number | null;
  courtLocation?: string | null;
  name?: string;
  playerCount?: number;
}

interface SessionCalendarProps {
  sessions: CalendarSession[];
  isLoading?: boolean;
  onSessionClick?: (session: CalendarSession) => void;
  onMonthChange?: (year: number, month: number) => void;
  variant?: 'parent' | 'coach' | 'director';
}

// Format date to YYYY-MM-DD in local timezone
function formatDateKey(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function formatDateDisplay(date: Date): string {
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
}

function getDateStatus(date: Date): 'past' | 'today' | 'future' {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const compareDate = new Date(date);
  compareDate.setHours(0, 0, 0, 0);

  if (compareDate.getTime() === today.getTime()) return 'today';
  if (compareDate < today) return 'past';
  return 'future';
}

// Calculate which row (0-indexed) a date appears in within the calendar grid
function getCalendarRow(date: Date, displayedMonth: Date): number {
  // Get the first day of the displayed month
  const firstOfMonth = new Date(displayedMonth.getFullYear(), displayedMonth.getMonth(), 1);
  const firstDayOfWeek = firstOfMonth.getDay(); // 0 = Sunday

  // Calculate the date's position relative to the calendar grid start
  const dayOfMonth = date.getDate();
  const monthDiff = date.getMonth() - displayedMonth.getMonth() +
    (date.getFullYear() - displayedMonth.getFullYear()) * 12;

  if (monthDiff < 0) {
    // Date is from previous month (shown in first row)
    return 0;
  } else if (monthDiff > 0) {
    // Date is from next month (shown in last rows)
    const daysInMonth = new Date(displayedMonth.getFullYear(), displayedMonth.getMonth() + 1, 0).getDate();
    return Math.floor((daysInMonth - 1 + firstDayOfWeek) / 7);
  }

  // Date is in the displayed month
  return Math.floor((dayOfMonth - 1 + firstDayOfWeek) / 7);
}

export function SessionCalendar({
  sessions,
  isLoading,
  onSessionClick,
  onMonthChange,
  variant = 'parent',
}: SessionCalendarProps) {
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(() => new Date());
  const [anchorRect, setAnchorRect] = useState<{ top: number; left: number; width: number; height: number } | null>(null);
  const calendarRef = useRef<HTMLDivElement>(null);
  const anchorRef = useRef<HTMLDivElement>(null);

  // Determine popover position based on day of week and row
  // Horizontal: Sun-Wed = right, Thu-Sat = left
  // Vertical: top 2 rows = bottom, bottom 3 rows = top
  const getPopoverSide = (): 'top' | 'bottom' | 'left' | 'right' => {
    if (!selectedDate) return 'right';

    const row = getCalendarRow(selectedDate, calendarMonth);

    // Top 2 rows (0, 1): show below
    // Bottom rows (2+): show above
    if (row <= 1) {
      return 'bottom';
    }
    return 'top';
  };

  const getPopoverAlign = (): 'start' | 'center' | 'end' => {
    if (!selectedDate) return 'center';

    const dayOfWeek = selectedDate.getDay();
    // Sun, Mon, Tue (0-2): align start (left)
    // Wed, Thu (3-4): align center
    // Fri, Sat (5-6): align end (right)
    if (dayOfWeek <= 2) return 'start';
    if (dayOfWeek >= 5) return 'end';
    return 'center';
  };

  const popoverSide = getPopoverSide();
  const popoverAlign = getPopoverAlign();

  const handleMonthChange = (newMonth: Date) => {
    setCalendarMonth(newMonth);
    onMonthChange?.(newMonth.getFullYear(), newMonth.getMonth());
  };

  // Helper to get date from session (handles both date field and startTime with datetime)
  const getSessionDate = (session: CalendarSession): string => {
    if (session.date) return session.date;
    // Extract date from startTime (format: YYYY-MM-DDTHH:MM:SS)
    return session.startTime.split('T')[0];
  };

  // Build session dates map
  const sessionDates = useMemo(() => {
    const dates = new Map<string, CalendarSession[]>();
    sessions.forEach(session => {
      const dateKey = getSessionDate(session);
      if (!dates.has(dateKey)) {
        dates.set(dateKey, []);
      }
      dates.get(dateKey)!.push(session);
    });
    return dates;
  }, [sessions]);

  // Get sessions for selected date
  const selectedDateSessions = useMemo(() => {
    if (!selectedDate) return [];
    const dateKey = formatDateKey(selectedDate);
    return sessionDates.get(dateKey) || [];
  }, [selectedDate, sessionDates]);

  // Modifiers for calendar dots
  const modifiers = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const sessionDateObjects = Array.from(sessionDates.keys()).map(dateStr => {
      const [year, month, day] = dateStr.split('-').map(Number);
      return new Date(year, month - 1, day);
    });

    return {
      pastSession: sessionDateObjects.filter(d => d < today),
      upcomingSession: sessionDateObjects.filter(d => d >= today),
    };
  }, [sessionDates]);

  const handleDayClick = (date: Date, _modifiers: Record<string, boolean>, e: React.MouseEvent) => {
    // Get the clicked button's position relative to the calendar container
    const button = e.currentTarget as HTMLElement;
    const calendarContainer = calendarRef.current;
    if (button && calendarContainer) {
      const containerRect = calendarContainer.getBoundingClientRect();
      const buttonRect = button.getBoundingClientRect();
      setAnchorRect({
        top: buttonRect.top - containerRect.top,
        left: buttonRect.left - containerRect.left,
        width: buttonRect.width,
        height: buttonRect.height,
      });
      setSelectedDate(date);
      setPopoverOpen(true);

      // When switching days while popover is already open, floating-ui doesn't
      // detect that the anchor moved (CSS position change). Dispatch a resize
      // event to trigger its autoUpdate listener and recalculate position.
      if (popoverOpen) {
        requestAnimationFrame(() => {
          window.dispatchEvent(new Event('resize'));
        });
      }
    }
  };

  // Prevent Radix from dismissing the popover when interacting inside the calendar.
  // Covers both onPointerDownOutside and onFocusOutside (day buttons steal focus).
  // This lets handleDayClick fire normally and switch days in a single click.
  const handleInteractOutside = useCallback((e: Event) => {
    // Radix stores the real browser event in detail.originalEvent
    const detail = (e as CustomEvent).detail;
    const originalTarget = detail?.originalEvent?.target as Node | null;
    // Fall back to e.target if detail is unavailable
    const target = originalTarget || (e.target as Node | null);
    if (calendarRef.current && target && calendarRef.current.contains(target)) {
      e.preventDefault();
    }
  }, []);

  const handleSessionClick = (session: CalendarSession) => {
    setPopoverOpen(false);
    onSessionClick?.(session);
  };

  const dotColor = variant === 'coach' ? 'bg-blue-500' : variant === 'director' ? 'bg-purple-500' : 'bg-green-500';

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-4">
          <Skeleton className="h-[300px] w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="p-4">
        {/* Legend */}
        <div className="flex justify-center gap-4 mb-4 text-xs">
          <div className="flex items-center gap-1.5">
            <div className="w-4 h-4 rounded bg-accent border" />
            <span className="text-muted-foreground">Today</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className={cn("w-2 h-2 rounded-full", dotColor)} />
            <span className="text-muted-foreground">Session</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full bg-gray-400" />
            <span className="text-muted-foreground">Past</span>
          </div>
        </div>

        {/* Calendar with Popover */}
        <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
          <div ref={calendarRef} className="relative">
            <CalendarComponent
              mode="single"
              selected={selectedDate}
              onDayClick={handleDayClick}
              month={calendarMonth}
              onMonthChange={handleMonthChange}
              modifiers={modifiers}
              modifiersClassNames={{
                pastSession: 'relative after:absolute after:bottom-1 after:left-1/2 after:-translate-x-1/2 after:w-2 after:h-2 after:rounded-full after:bg-gray-400',
                upcomingSession: cn(
                  'relative after:absolute after:bottom-1 after:left-1/2 after:-translate-x-1/2 after:w-2 after:h-2 after:rounded-full',
                  variant === 'coach' ? 'after:bg-blue-500' : variant === 'director' ? 'after:bg-purple-500' : 'after:bg-green-500'
                ),
              }}
              className="w-full [--cell-size:min(3rem,calc((100vw-5rem)/7))] [&_button]:border [&_button]:border-border"
            />
            {/* Invisible anchor positioned over the clicked date cell */}
            {anchorRect && (
              <PopoverAnchor
                ref={anchorRef}
                className="absolute pointer-events-none"
                style={{
                  top: anchorRect.top,
                  left: anchorRect.left,
                  width: anchorRect.width,
                  height: anchorRect.height,
                }}
              />
            )}
          </div>

          <PopoverContent
            className="w-72 max-w-[calc(100vw-1rem)] p-0"
            align={popoverAlign}
            side={popoverSide}
            sideOffset={8}
            onInteractOutside={handleInteractOutside}
          >
            {selectedDate && (
              <div className="p-4">
                {/* Header */}
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-sm">
                    {formatDateDisplay(selectedDate)}
                  </h3>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={() => setPopoverOpen(false)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>

                {/* Sessions */}
                {selectedDateSessions.length === 0 ? (
                  <div className="text-center py-4">
                    <Calendar className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">No sessions scheduled</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[200px] overflow-y-auto">
                    {selectedDateSessions.map(session => {
                      const sessionDate = getSessionDate(session);
                      const status = getDateStatus(new Date(sessionDate + 'T00:00:00'));
                      // Format time - handle both "14:00" and "2024-01-31T14:00:00" formats
                      const formatTime = (timeStr: string) => {
                        if (timeStr.includes('T')) {
                          return timeStr.split('T')[1].substring(0, 5);
                        }
                        return timeStr.substring(0, 5);
                      };
                      return (
                        <div
                          key={session.id}
                          onClick={() => handleSessionClick(session)}
                          className={cn(
                            "p-3 rounded-lg cursor-pointer transition-colors",
                            status === 'past' && "bg-muted/50 hover:bg-muted",
                            status === 'today' && "bg-muted/50 hover:bg-muted border border-border",
                            status === 'future' && "bg-muted/50 hover:bg-muted border border-border"
                          )}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium text-sm text-foreground">
                                {formatTime(session.startTime)} - {formatTime(session.endTime)}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {session.name || session.sessionType || 'Training'}
                                {session.courtLocation && ` • ${session.courtLocation}`}
                                {session.courtNumber && !session.courtLocation && ` • Court ${session.courtNumber}`}
                                {session.playerCount !== undefined && ` • ${session.playerCount} players`}
                              </p>
                            </div>
                            <Badge
                              variant="outline"
                              className={cn(
                                "text-xs",
                                status === 'past' && "text-muted-foreground border-border",
                                status === 'today' && "text-primary border-primary/40",
                                status === 'future' && "text-foreground border-border"
                              )}
                            >
                              {status === 'past' ? 'Past' : status === 'today' ? 'Today' : 'Upcoming'}
                            </Badge>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

              </div>
            )}
          </PopoverContent>
        </Popover>
      </CardContent>
    </Card>
  );
}
